import { createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { ActivityFeed, type ActivityItem } from "@/components/ActivityFeed";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

/**
 * /activity — global activity feed. Implements designs/Activity.html.
 *
 * Layout: pg-head + sub-caption + filter chips + day-grouped event timeline.
 * The feed itself (chips, grouping, filter, "Load more") lives in
 * <ActivityFeed>, which is also used by the per-project activity page.
 */
function ActivityPage() {
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: queryKeys.activityGlobal(),
    queryFn: ({ pageParam }) =>
      api.listActivityGlobal(pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items: ActivityItem[] = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="pg-wrap">
      <div className="pg-head">
        <h1>Activity</h1>
      </div>
      <div className="pg-sub">
        Everything happening across your projects — so progress stays visible to
        the whole team.
      </div>

      {q.isLoading ? (
        <Skeleton count={5} height={56} style={{ marginBottom: 8 }} />
      ) : q.isError ? (
        <EmptyState
          icon="bell"
          title="Couldn't load activity"
          caption="Something went wrong fetching the feed. Try again in a moment."
          actionLabel="Retry"
          onAction={() =>
            void qc.invalidateQueries({ queryKey: queryKeys.activityGlobal() })
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon="bell"
          title="No activity yet"
          caption="When teammates create tasks, post comments, or upload files, you'll see it here."
        />
      ) : (
        <ActivityFeed
          items={items}
          hasMore={!!q.hasNextPage}
          onLoadMore={() => q.fetchNextPage()}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/activity")({ component: ActivityPage });
